import LPCertificateCard from '@berlitzplatforms/micro.ui.lp-certificate-card'
import LPCTCard, { LPCTCardProps } from '@berlitzplatforms/micro.ui.lpct-card/lib'
import PortalLearningPath from '@classes/LearningPath'
import PortalLearningPathUnit from '@classes/LearningPathUnit'
import PortalStudentProfile from '@classes/StudentProfile'
import Collapse from '@components/Collapse'
import { LoadingModal, WarningModal } from '@components/Modal'
import useCTCardState from '@hooks/useCTCardState'
import { StoreProps } from '@interfaces/StoreState'
import { RegistrationWrapper } from '@interfaces/Student'
import CollapseHeader from '@layouts/LearningPath/Layout/CollapseHeader'
import { LpCtCardOverride, TileRow } from '@layouts/LearningPath/Layout/style'
import Router from 'next/router'
import React, { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import PanelLoader from './PanelLoader'
import PanelTile from './PanelTile'
import useAchievements from '@hooks/useAchievements'
export interface CTPanelPathProps {
  Path: PortalLearningPath
  Registration: RegistrationWrapper
  StudentProfile?: PortalStudentProfile
}
const CTPanelPath: React.FC<CTPanelPathProps> = ({ Path, Registration, StudentProfile }) => {
  const intl = useIntl()
  const [materialName, setMaterialName] = useState('')
  const [openErrorModal, setOpenErrorModal] = useState(false)
  const { tempProfile, activeRegistration } = useSelector(({ userProfile, dashboard }: StoreProps) => {
    const tempProfile = new PortalStudentProfile(userProfile.info?.StudentProfile?.StudentProfileInfo)
    const activeRegistration = tempProfile.getRegistrationById(dashboard?.activeRegistration || '')?.RegistrationInfo
    return {
      tempProfile,
      activeRegistration,
    }
  })
  const studentProfile = useMemo(() => StudentProfile || tempProfile, [StudentProfile, tempProfile])
  const [getCTState, { data, loading, btnLoading, error }] = useCTCardState()
  const { loading: achievementLoading, Achievements } = useAchievements()
  useEffect(() => {
    if (activeRegistration && Path) {
      const languageLevel = Path?.Material?.MaterialLevel || ''
      const language = Path?.Material?.language || ''
      setMaterialName(`Curriculum Test ${language} ${languageLevel}`)
      getCTState({
        username: studentProfile.student.RecordId,
        url: Path.NonCultureUnits?.[0]?.Activities?.[0]?.Url || '',
        registration: activeRegistration,
        learningPath: Path,
      })
    }
  }, [activeRegistration, Path])
  useEffect(() => {
    setOpenErrorModal(!!error)
  }, [error])
  return loading || achievementLoading ? (
    <PanelLoader />
  ) : (
    <>
      <Collapse.Panel
        key={Path.Id}
        id={Path.Id.toString()}
        header={
          <CollapseHeader
            title={materialName}
            language={Path.Language}
            materialType={Path.learningPath?.details?.material_type}
            isFlex={Boolean(Path.IsFlex)}
            path={Path}
          />
        }
      >
        <TileRow id={`scroller-${Path.Id}`}>
          {Path.NonCultureUnits?.map((Unit, i) => {
            return (
              <PanelTile Unit={Unit as PortalLearningPathUnit} Path={Path} key={Unit?.Id}>
                {!i ? (
                  <LpCtCardOverride status={data?.state ?? ''}>
                    <LPCTCard {...(data as LPCTCardProps)} mode="card"></LPCTCard>
                  </LpCtCardOverride>
                ) : (
                  <>
                    {Achievements.length > 0 && (
                      <LPCertificateCard
                        title={intl.formatMessage({
                          id: 'Certificate of Achievement',
                          defaultMessage: 'Certificate of Achievement',
                        })}
                        subtitle={intl.formatMessage(
                          {
                            id: 'Congratulations {name}!',
                            defaultMessage: 'Congratulations {name}!',
                          },
                          {
                            name: studentProfile.student.FirstName,
                          }
                        )}
                        onBtnClick={() => {
                          Router.push('/settings/certificates')
                        }}
                        btnLabel={intl.formatMessage({ id: 'View', defaultMessage: 'View' })}
                      />
                    )}
                  </>
                )}
              </PanelTile>
            )
          })}
        </TileRow>
      </Collapse.Panel>
      <LoadingModal
        open={btnLoading}
        onClose={() => {}}
        title={`${intl.formatMessage({ id: 'Please wait a moment' })}.`}
      />
      <WarningModal open={openErrorModal} onClose={() => setOpenErrorModal(false)} anApiError noButtons />
    </>
  )
}
export default React.memo(CTPanelPath)
